import Box from '@liquidweb-dev/undercurrent/Box';
import RadioTile, {
	type RadioTileProps,
} from '@liquidweb-dev/undercurrent/RadioTile';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { FixedColorSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export interface SelectionCardProps extends RadioTileProps {
	/** Icon component to display for the add-on */
	Icon: (props: FixedColorSvgProps & { monochrome?: boolean }) => JSX.Element;
	/** Display title for the add-on */
	title: string;
	/** Descriptive text explaining the add-on features */
	description: string;
}

/**
 * Inner content component for the selection card.
 */
const SelectionCardContent = ({
	Icon,
	title,
	description,
	...radioTileProps
}: Omit<SelectionCardProps, 'disabledReason'>) => {
	return (
		<RadioTile
			{...radioTileProps}
			sx={combineSx({ padding: 300 }, radioTileProps.sx)}
		>
			<Stack gap={100}>
				<Stack direction="row" gap={100} alignItems="center">
					<Icon
						sx={({ size }) => ({
							height: size.sm,
							width: size.sm,
						})}
						monochrome
					/>
					<Typography variant="title-xs" fontWeight={500}>
						{title}
					</Typography>
				</Stack>
				<Typography
					variant="body-sm"
					sx={({ vars }) => ({ color: vars.palette.uc.text.weak })}
				>
					{description}
				</Typography>
			</Stack>
		</RadioTile>
	);
};

/**
 * Selection card component for displaying server protection add-on options.
 * Renders as a radio tile with an icon, title, and description.
 * When disabled, wraps with a fancy tooltip to explain why.
 */
const SelectionCard = ({ disabled, ...rest }: SelectionCardProps) => (
	<Tooltip
		arrow
		variant="fancy"
		placement="top"
		title={disabled ? 'Why is this disabled?' : undefined}
		message={
			disabled ? "This server type isn't supported by this add-on." : undefined
		}
	>
		<Box>
			<SelectionCardContent disabled={disabled} {...rest} />
		</Box>
	</Tooltip>
);

export default SelectionCard;
